import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Style values applied to a card at a given distance ("offset") from the
 * centered card. Offsets 1/2 are the near/far card on the right; the left
 * side mirrors them with a negated x and rotation.
 */
const OFFSET_STYLES = {
  0: { x: 0, scale: 1, rotate: 0, opacity: 1, z: 30, brightness: 1, blur: 0 },
  1: { x: 285, scale: 0.84, rotate: -24, opacity: 0.65, z: 20, brightness: 0.75, blur: 0 },
  2: { x: 510, scale: 0.68, rotate: -38, opacity: 0.38, z: 10, brightness: 0.55, blur: 1 },
};

const HIDDEN_STYLE = { x: 0, scale: 0.4, rotate: 0, opacity: 0, z: 0, brightness: 0.4, blur: 2 };

/**
 * @param {number} offset
 * @param {number} total
 */
function styleForOffset(offset, total) {
  if (offset === 0) return OFFSET_STYLES[0];
  if (offset === 1) return OFFSET_STYLES[1];
  if (offset === 2) return OFFSET_STYLES[2];
  if (offset === total - 1) return { ...OFFSET_STYLES[1], x: -285, rotate: 24 };
  if (offset === total - 2) return { ...OFFSET_STYLES[2], x: -510, rotate: 38 };
  return HIDDEN_STYLE;
}

/**
 * A 3D coverflow carousel: the centered card is full size, the two cards on
 * either side recede in scale/rotation/opacity, matching CSS transitions do
 * the actual animating (this class only sets custom properties + classes).
 *
 * @extends Component
 */
export default class CoverflowCarousel extends Component {
  requiredRefs = ['stage', 'prevButton', 'nextButton', 'dots', 'ambienceImage'];

  connectedCallback() {
    super.connectedCallback();

    this.currentIndex = 0;
    /** @type {HTMLButtonElement[]} */
    this.dotButtons = [];
    /** @type {ReturnType<typeof setInterval> | null} */
    this.autoplayTimer = null;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    this.ambienceTimer = undefined;

    this.#buildDots();
    this.render();

    this.refs.prevButton.addEventListener('click', this.#handlePrevClick);
    this.refs.nextButton.addEventListener('click', this.#handleNextClick);
    this.refs.stage.addEventListener('click', this.#handleStageClick);
    this.addEventListener('pointerenter', this.#pauseAutoplay);
    this.addEventListener('pointerleave', this.#resumeAutoplay);
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd);
    window.addEventListener('keydown', this.#handleKeydown);

    this.#resumeAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stopAutoplay();
    clearTimeout(this.ambienceTimer);
    this.refs.prevButton.removeEventListener('click', this.#handlePrevClick);
    this.refs.nextButton.removeEventListener('click', this.#handleNextClick);
    this.refs.stage.removeEventListener('click', this.#handleStageClick);
    this.removeEventListener('pointerenter', this.#pauseAutoplay);
    this.removeEventListener('pointerleave', this.#resumeAutoplay);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchend', this.#handleTouchEnd);
    window.removeEventListener('keydown', this.#handleKeydown);
  }

  get cards() {
    return /** @type {HTMLElement[]} */ (this.refs.cards ?? []);
  }

  /** @param {number} index */
  goTo(index) {
    const total = this.cards.length;
    if (total === 0) return;

    this.currentIndex = ((index % total) + total) % total;
    this.render();
    this.#resumeAutoplay();
  }

  next = () => this.goTo(this.currentIndex + 1);
  prev = () => this.goTo(this.currentIndex - 1);

  render() {
    const cards = this.cards;
    const total = cards.length;
    if (total === 0) return;

    cards.forEach((card, index) => {
      const offset = (index - this.currentIndex + total) % total;
      const isCenter = offset === 0;
      const style = styleForOffset(offset, total);

      card.style.setProperty('--cf-x', `${style.x}px`);
      card.style.setProperty('--cf-scale', String(style.scale));
      card.style.setProperty('--cf-rotate', `${style.rotate}deg`);
      card.style.setProperty('--cf-opacity', String(style.opacity));
      card.style.setProperty('--cf-z', String(style.z));
      card.style.setProperty('--cf-brightness', String(style.brightness));
      card.style.setProperty('--cf-blur', `${style.blur}px`);
      card.classList.toggle('is-center', isCenter);
      card.setAttribute('aria-hidden', isCenter ? 'false' : 'true');
      card.tabIndex = isCenter ? 0 : -1;
    });

    this.#setAmbiencePhoto(cards[this.currentIndex]?.dataset.photo);

    this.dotButtons.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', String(isActive));
    });
  }

  /** @param {string | undefined} url */
  #setAmbiencePhoto(url) {
    const image = this.refs.ambienceImage;
    if (!url || url === image.dataset.currentSrc) return;

    image.dataset.currentSrc = url;
    image.style.opacity = '0';

    clearTimeout(this.ambienceTimer);
    this.ambienceTimer = setTimeout(() => {
      image.src = url;
      requestAnimationFrame(() => {
        image.style.opacity = '1';
      });
    }, 260);
  }

  #buildDots() {
    const container = this.refs.dots;
    container.textContent = '';
    this.dotButtons = this.cards.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'coverflow__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.addEventListener('click', () => this.goTo(index));
      container.appendChild(dot);
      return dot;
    });
  }

  #handlePrevClick = () => this.prev();
  #handleNextClick = () => this.next();

  /** @param {MouseEvent} event */
  #handleStageClick = (event) => {
    const card = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('.coverflow__card') : null);
    if (!card || card.classList.contains('is-center')) return;

    const index = this.cards.indexOf(card);
    if (index !== -1) this.goTo(index);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (!this.isConnected) return;
    if (event.key === 'ArrowLeft') this.prev();
    if (event.key === 'ArrowRight') this.next();
  };

  #touchStartX = 0;

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    this.#touchStartX = event.touches[0]?.clientX ?? 0;
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    const endX = event.changedTouches[0]?.clientX ?? this.#touchStartX;
    const diff = endX - this.#touchStartX;
    if (Math.abs(diff) <= 45) return;
    if (diff < 0) this.next();
    else this.prev();
  };

  #pauseAutoplay = () => this.#stopAutoplay();

  #resumeAutoplay = () => {
    this.#stopAutoplay();

    if (this.dataset.autoplay !== 'true' || prefersReducedMotion() || this.cards.length <= 1) return;

    const delay = Number(this.dataset.autoplayDelay) || 5000;
    this.autoplayTimer = setInterval(() => this.next(), delay);
  };

  #stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

if (!customElements.get('coverflow-carousel')) {
  customElements.define('coverflow-carousel', CoverflowCarousel);
}
